import classnames from 'classnames'
import * as React from 'react'
import * as ReactDOM from 'react-dom'
import FocusLock from 'react-focus-lock'

import SvgSprite from 'airship/patterns/svg-sprite'

import {DMSize} from '../types'

export type ModalSize = DMSize

export const modalSizeArray = ['sm', 'md', 'lg', 'xl'] as const

export interface Props {
  isOpen: boolean
  children: React.ReactNode
  /** size of modal; defaults to `lg` which is a fullscreen modal */
  size?: ModalSize
  onClose: () => void
  /** normally the modal is positioned in the page, and will be affected
   by scrolling the page. This makes it positioned `fixed` and it
   will not be affected by scroll.*/
  positionFixed?: boolean
  closeText?: string
  /** hides the close modal button  */
  hideClose?: boolean
  /** Controls whether or not an input will be automatically focused on modal open */
  disableAutoFocus?: boolean
  displayClasses?: string
  windowClasses?: string
  contentClasses?: string
}

export const MODAL_IDENTIFIER = 'modal-window'

const Modal: React.FunctionComponent<Props> = ({
  isOpen,
  children,
  onClose,
  size = 'lg',
  positionFixed = false,
  closeText = 'Close',
  hideClose = false,
  disableAutoFocus = false,
  displayClasses,
  windowClasses,
  contentClasses,
}) => {
  const classList = [MODAL_IDENTIFIER, size]
  if (positionFixed) classList.push('fixed')
  const classes = classnames(...classList, windowClasses)
  const contentClassList =
    size === 'xl'
      ? classnames('modal-big-content', contentClasses)
      : classnames('modal-content', contentClasses)

  const onCloseClicked = React.useCallback(() => onClose(), [onClose])

  return isOpen
    ? ReactDOM.createPortal(
        // eslint-disable-next-line jsx-a11y/no-autofocus
        <FocusLock autoFocus={!disableAutoFocus}>
          <div className={displayClasses}>
            <div className="modal-wrap">
              <div className="modal-overlay" />
              <div className={classes}>
                {!hideClose && (
                  <button className="modal-close" onClick={onCloseClicked}>
                    <span className="u-grey u-space-right-06 modal-close--text">
                      {closeText}
                    </span>
                    <SvgSprite
                      name="remove"
                      color="grey-60"
                      displayClasses="u-text-grow-6"
                    />
                  </button>
                )}
                <div className={contentClassList} role="document">
                  {children}
                </div>
              </div>
            </div>
          </div>
        </FocusLock>,
        document.body
      )
    : null
}

export default Modal
